import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import PanelLinks from "@/components/feature/panel/PanelLinks";
import StudentSelector from "@/pages/ogretmen/components/StudentSelector";
import { useTeacherStudents, useTeacherStudentAiProfile } from "@/hooks/useTeacher";
import {useSiteSettingsContext} from "@/hooks/useSiteSettingsContext";

export default function AiGecmisi() {
  const { settings } = useSiteSettingsContext();
  const name = settings.siteName || "DersDinle";
  const { data: students, loading: studentsLoading, error: studentsError } = useTeacherStudents();
  const [activeUserId, setActiveUserId] = useState<string | null>(null);

  const list = students ?? [];
  const activeStudent = list.find((s) => s.userId === activeUserId) ?? list[0] ?? null;

  const { data, loading, error, reload } = useTeacherStudentAiProfile(activeStudent?.userId ?? null);

  if (studentsLoading) return <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />;

  if (studentsError) {
    return (
      <div className="space-y-6">
        <SectionHeader title="AI Öğrenci Geçmişi" description="Öğrencilerinin yapay zekâ asistanıyla çalışma geçmişi." />
        <EmptyState icon="ri-error-warning-line" title="Veriler yüklenemedi" description={studentsError} />
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="space-y-6">
        <SectionHeader title="AI Öğrenci Geçmişi" description="Öğrencilerinin yapay zekâ asistanıyla çalışma geçmişi." />
        <EmptyState
          icon="ri-brain-line"
          title="Henüz öğrenci yok"
          description='"Öğrencilerim" sayfasından öğrenci atadığında AI geçmişi burada görünecek.'
        />
        <PanelLinks
          links={[{ label: "Öğrencilerim", href: "/ogretmen/ogrencilerim", icon: "ri-group-line" }]}
        />
      </div>
    );
  }

  const analysis = data;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="AI Öğrenci Geçmişi"
        description={`${activeStudent?.fullName ?? "Öğrencinin"} ${name} Asistan ile çalışma geçmişi ve AI tespitleri.`}
      />
      <StudentSelector
        students={list}
        activeUserId={activeStudent?.userId ?? null}
        onSelect={setActiveUserId}
      />

      {loading ? (
        <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />
      ) : error ? (
        <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
          <p className="text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={reload}
            className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            Tekrar dene
          </button>
        </div>
      ) : !analysis ||
        (!analysis.summary &&
          analysis.strongSubjects.length === 0 &&
          analysis.recentNotes.length === 0) ? (
        <EmptyState
          icon="ri-brain-line"
          title="Henüz AI geçmişi yok"
          description={`Öğrenci ${name} Asistan'ı kullandıkça çalışma geçmişi ve AI tespitleri burada görünecek.`}
        />
      ) : (
        <>
          {analysis.summary && (
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
                <i className="ri-brain-line text-primary-600" aria-hidden="true"></i>
                AI Değerlendirmesi
              </p>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                {analysis.summary}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading font-semibold text-base text-foreground-950">Öğrenme Profili</h2>
              <dl className="mt-3 space-y-2 text-sm">
                {analysis.learningLevel && (
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">Seviye</dt>
                    <dd className="font-medium text-foreground-900">{analysis.learningLevel}</dd>
                  </div>
                )}
                {analysis.motivationLevel > 0 && (
                  <div className="flex items-center justify-between gap-3">
                    <dt className="text-foreground-500">Motivasyon</dt>
                    <dd className="font-medium text-foreground-900">{analysis.motivationLevel}/10</dd>
                  </div>
                )}
              </dl>

              <h3 className="mt-5 text-sm font-semibold text-foreground-800">Güçlü Yönler</h3>
              {analysis.strongSubjects.length === 0 ? (
                <p className="mt-2 text-sm text-foreground-500">Henüz belirlenmedi.</p>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {analysis.strongSubjects.map((s) => (
                    <li key={s} className="flex items-center gap-2 text-sm text-accent-800">
                      <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                      {s}
                    </li>
                  ))}
                </ul>
              )}

              <h3 className="mt-5 text-sm font-semibold text-foreground-800">Destek Gerekenler</h3>
              {analysis.weakSubjects.length === 0 ? (
                <p className="mt-2 text-sm text-foreground-500">Henüz belirlenmedi.</p>
              ) : (
                <ul className="mt-2 space-y-1.5">
                  {analysis.weakSubjects.map((s) => (
                    <li key={s} className="flex items-center gap-2 text-sm text-secondary-900">
                      <i className="ri-alert-line" aria-hidden="true"></i>
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading font-semibold text-base text-foreground-950">Son AI Çalışmaları</h2>
              {analysis.recentNotes.length === 0 ? (
                <p className="mt-3 text-sm text-foreground-500">Henüz kayıt yok.</p>
              ) : (
                <ul className="mt-3 space-y-2 max-h-[420px] overflow-y-auto">
                  {analysis.recentNotes.map((n, i) => (
                    <li key={i} className="rounded-md bg-background-100/60 px-3 py-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-primary-700">{n.date}</span>
                        <span className="text-xs text-foreground-400 uppercase">{n.type}</span>
                      </div>
                      <p className="mt-1 text-sm text-foreground-700 leading-relaxed">{n.content}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      <PanelLinks
        links={[
          { label: "Öğrenci Analizleri", href: "/ogretmen/ogrenci-analizleri", icon: "ri-line-chart-line" },
          { label: "Öğrencilerim", href: "/ogretmen/ogrencilerim", icon: "ri-group-line" },
        ]}
      />
    </div>
  );
}